// Nutri / Návrhy receptů — centrální přehled receptů navržených klientkami
// (20.8.2026, zadání Ládi). Dosud se návrhy schvalovaly jen v detailu klientky
// (Výživa → Jídelníček), takže chyběl jeden seznam přes všechny: stav, kdo
// navrhl, základní info a hlavně možnost recept ZPĚTNĚ upravit.
//
// Úprava schváleného receptu přepíše i jeho blok ve znalostní bázi
// (jidelnicek_reference), takže generátor jídelníčků hned vaří z nové verze.
import React, { useMemo, useState } from "react";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, RozbalovaciVyber, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { toast } from "../../components/Toast.jsx";
import {
  ChefHat, Check, X, Pencil, Loader2, Search, User, Calculator, ChevronDown, ChevronRight,
} from "lucide-react";

const STAV_META = {
  ceka: { label: "Čeká na schválení", pill: "bg-amber-50 text-amber-800 border-amber-200", tecka: "bg-amber-400" },
  schvaleno: { label: "Schváleno", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", tecka: "bg-emerald-500" },
  zamitnuto: { label: "Zamítnuto", pill: "bg-slate-100 text-slate-500 border-slate-200", tecka: "bg-slate-300" },
};

const CHODY = ["snídaně", "svačina", "oběd", "večeře"];

const fmtDatum = (d) => (d ? new Date(d).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric" }) : "");

function Makra({ makra }) {
  if (!makra || !makra.kcal) return <span className="text-xs text-slate-400">makra nedopočtena</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs tabular-nums text-slate-600">
      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold">{makra.kcal} kcal</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5">B {makra.b} g</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5">S {makra.s} g</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5">T {makra.t} g</span>
    </span>
  );
}

// Editační formulář jednoho návrhu — název, chod, ingredience, postup.
function EditForm({ navrh, onDone, onCancel }) {
  const [form, setForm] = useState({
    nazev: navrh.nazev || "",
    chod: navrh.chod || "",
    ingredience: navrh.ingredience || "",
    postup: navrh.postup || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const ulozit = async () => {
    if (!form.nazev.trim()) { toast("Název nesmí být prázdný", "error"); return; }
    setSaving(true);
    try {
      const r = await apiFetch("/api/jidelnicek", {
        method: "POST",
        body: JSON.stringify({ action: "recept-upravit", id: navrh.id, ...form }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      toast(
        j.bazeAktualizovana
          ? "Recept upraven, znalostní báze přepsána na novou verzi."
          : "Recept upraven.",
        "success"
      );
      onDone();
    } catch (e) {
      toast(e.message, "error");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 space-y-3 rounded-xl border border-brand-200 bg-brand-50/50 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Název</span>
          <input className="field mt-1" value={form.nazev} onChange={set("nazev")} />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Chod</span>
          <select className="field mt-1 bg-white" value={form.chod} onChange={set("chod")}>
            {!CHODY.includes(form.chod) && form.chod && <option value={form.chod}>{form.chod}</option>}
            {CHODY.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <label className="block">
        <span className="text-xs font-semibold text-slate-600">Ingredience (jedna na řádek)</span>
        <textarea className="field mt-1 resize-none" rows={4} value={form.ingredience} onChange={set("ingredience")} />
      </label>
      <label className="block">
        <span className="text-xs font-semibold text-slate-600">Postup</span>
        <textarea className="field mt-1 resize-none" rows={3} value={form.postup} onChange={set("postup")} />
      </label>
      {navrh.stav === "schvaleno" && (
        <p className="text-xs text-brand-800">
          Recept je schválený, uložením se přepíše i ve znalostní bázi a generátor bude vařit z nové verze.
        </p>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} disabled={saving}
          className="btn btn-secondary">
          Zrušit
        </button>
        <button type="button" onClick={ulozit} disabled={saving}
          className="btn btn-primary">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Uložit
        </button>
      </div>
    </div>
  );
}

function NavrhKarta({ navrh, onChanged }) {
  const [open, setOpen] = useState(navrh.stav === "ceka");
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState("");
  const meta = STAV_META[navrh.stav] || STAV_META.ceka;

  const akce = async (action, okMsg) => {
    setBusy(action);
    try {
      const r = await apiFetch("/api/jidelnicek", {
        method: "POST",
        body: JSON.stringify({ action, id: navrh.id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Akce selhala");
      toast(okMsg, "success");
      onChanged();
    } catch (e) {
      toast(e.message, "error");
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 text-left">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${meta.tecka}`} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-slate-900">{navrh.nazev}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {navrh.klient_name || "neznámá klientka"}</span>
            <span aria-hidden className="h-3 w-px bg-slate-300" />
            <span>{navrh.chod || "bez chodu"}</span>
            <span aria-hidden className="h-3 w-px bg-slate-300" />
            <span>{fmtDatum(navrh.created_at)}</span>
            {navrh.resil && (
              <>
                <span aria-hidden className="h-3 w-px bg-slate-300" />
                <span>vyřídil(a) {navrh.resil}</span>
              </>
            )}
          </span>
        </span>
        <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.pill}`}>{meta.label}</span>
        {open ? <ChevronDown className="w-4 h-4 shrink-0 text-slate-400" /> : <ChevronRight className="w-4 h-4 shrink-0 text-slate-400" />}
      </button>

      {open && !edit && (
        <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
          <Makra makra={navrh.makra} />
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Ingredience</div>
              <div className="whitespace-pre-wrap text-sm text-slate-700">{navrh.ingredience || "—"}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Postup</div>
              <div className="whitespace-pre-wrap text-sm text-slate-700">{navrh.postup || "—"}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            <button type="button" onClick={() => setEdit(true)}
              className="btn btn-secondary">
              <Pencil className="w-3.5 h-3.5" /> Upravit
            </button>
            {navrh.stav === "ceka" && (
              <>
                <button type="button" disabled={!!busy}
                  onClick={() => akce("recept-makra", "Makra dopočtena.")}
                  className="btn btn-secondary">
                  {busy === "recept-makra" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />} Dopočítat makra
                </button>
                <button type="button" disabled={!!busy}
                  onClick={() => akce("recept-schvalit", "Recept schválen a přidán do báze.")}
                  className="btn btn-primary">
                  {busy === "recept-schvalit" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Schválit
                </button>
                <button type="button" disabled={!!busy}
                  onClick={() => akce("recept-zamitnout", "Recept zamítnut.")}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50">
                  {busy === "recept-zamitnout" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Zamítnout
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {open && edit && (
        <EditForm navrh={navrh} onCancel={() => setEdit(false)} onDone={() => { setEdit(false); onChanged(); }} />
      )}
    </div>
  );
}

export default function NavrhyReceptu() {
  const { data, loading, refetch } = useApiData("/api/jidelnicek?navrhy=1");
  const [filtr, setFiltr] = useState("vse"); // vse | ceka | schvaleno | zamitnuto
  const [hledej, setHledej] = useState("");

  const navrhy = useMemo(() => {
    const vse = Array.isArray(data?.navrhy) ? data.navrhy : [];
    const q = hledej.trim().toLowerCase();
    return vse
      .filter((n) => filtr === "vse" || n.stav === filtr)
      .filter((n) => !q || `${n.nazev} ${n.klient_name}`.toLowerCase().includes(q));
  }, [data, filtr, hledej]);

  const pocty = useMemo(() => {
    const vse = Array.isArray(data?.navrhy) ? data.navrhy : [];
    const p = { vse: vse.length, ceka: 0, schvaleno: 0, zamitnuto: 0 };
    for (const n of vse) if (p[n.stav] != null) p[n.stav]++;
    return p;
  }, [data]);

  return (
    <Stranka sirka="uzka">
      <GradientHeader
        icon={ChefHat}
        title="Návrhy receptů"
        napoveda="Recepty, které navrhly klientky v aplikaci v sekci Jídelníček. Schválený recept se přidá do databáze a generátor ho začne používat."
        loading={loading}
      />

      <div className="space-y-2">
        <RozbalovaciVyber
          stitek="Stav"
          aria-label="Filtr podle stavu návrhu"
          value={filtr}
          onChange={setFiltr}
          items={[
            { key: "vse", label: `Vše (${pocty.vse})` },
            { key: "ceka", label: `Čeká (${pocty.ceka})` },
            { key: "schvaleno", label: `Schváleno (${pocty.schvaleno})` },
            { key: "zamitnuto", label: `Zamítnuto (${pocty.zamitnuto})` },
          ]}
        />
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={hledej}
            onChange={(e) => setHledej(e.target.value)}
            placeholder="Hledat recept nebo klientku"
            className="field w-full pl-8 pr-3"
          />
        </div>
      </div>

      {loading && !data && <SkeletonRows rows={5} />}

      {!loading && !navrhy.length && (
        <EmptyState
          icon={ChefHat}
          title={pocty.vse === 0 ? "Zatím žádný návrh" : "Tomuhle filtru neodpovídá žádný návrh"}
          hint={pocty.vse === 0 ? "Návrhy chodí z klientské aplikace, ze sekce Jídelníček." : "Zkus jiný stav nebo vymaž hledání."}
        />
      )}

      <div className="space-y-3">
        {navrhy.map((n) => <NavrhKarta key={n.id} navrh={n} onChanged={refetch} />)}
      </div>
    </Stranka>
  );
}
